/**
 * CSV zoals de rest van de backoffice hem schrijft.
 *
 * <p>De conventie komt uit het productstatistiekenscherm en is niet vrijblijvend: UTF-8 mét BOM en
 * puntkomma als scheidingsteken, want zonder die twee opent Excel in het Nederlands een bestand vol
 * vraagtekens en alles in één kolom. Getallen met een komma als decimaalteken, en dan zonder
 * duizendtalscheiding — anders breekt de puntkomma-conventie op het duizendtal.
 *
 * <p>Hier staat het één keer, zodat een tweede exportknop niet zijn eigen versie van deze regels
 * krijgt. Een export die bij het ene scherm wél en bij het andere níét opent, is erger dan geen
 * export: dan ligt het aan de gebruiker, denkt de gebruiker.
 */

/** Eén cel: puntkomma's, aanhalingstekens en regeleindes ontsnapt volgens de csv-regels. */
export function csvCel(waarde: string | number | null | undefined): string {
  const t = waarde == null ? "" : String(waarde);
  return /[";\r\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
}

/** Een getal met een komma als decimaalteken en zonder duizendtalscheiding. */
export function csvGetal(n: number, locale = "nl-NL"): string {
  return n.toLocaleString(locale, { useGrouping: false, maximumFractionDigits: 2 });
}

/** Rijen naar één csv-tekst; elke rij is een lijst cellen die hier pas worden ontsnapt. */
export function csvTekst(rijen: (string | number | null | undefined)[][]): string {
  return rijen.map((rij) => rij.map(csvCel).join(";")).join("\r\n");
}

/**
 * Biedt de tekst als bestand aan.
 *
 * <p>De BOM staat er los voor, en niet in de tekst zelf: dan blijft die tekst bruikbaar voor iets
 * anders dan een download (een test die hem leest, bijvoorbeeld) zonder een onzichtbaar teken vooraan.
 */
export function csvDownload(tekst: string, bestandsnaam: string): void {
  const blob = new Blob(["﻿", tekst], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = bestandsnaam;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Niet meteen intrekken: sommige browsers zijn nog bezig met de download op het moment dat de
  // klik terugkeert.
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/**
 * De kop boven een export: waar het over gaat, wanneer het is gemaakt en welke filters er stonden.
 *
 * <p>Het functioneel ontwerp vraagt dit met zoveel woorden: "neem in het exportbestand minimaal mee:
 * titel van de analyse, uitvoerdatum, toegepaste filters, vestigingsscope en een korte definitie van
 * berekende velden. Zo blijft een export later interpreteerbaar."
 *
 * <p>Dat is geen formaliteit. Een los csv-bestand in iemands map, drie maanden later, is zonder deze
 * regels niet meer te plaatsen — en dan wordt er gerekend met cijfers waarvan niemand meer weet
 * waar ze over gingen.
 */
export function exportKop(opties: {
  titel: string;
  vraag: string;
  filters: string[];
  uitsplitsing: string[];
  vestiging?: string | null;
}): (string | number | null)[][] {
  const rijen: (string | number | null)[][] = [
    [opties.titel],
    ["Vraag", opties.vraag],
    ["Gemaakt op", new Date().toLocaleString("nl-NL")],
  ];
  if (opties.filters.length > 0) {
    rijen.push(["Filters", opties.filters.join(" · ")]);
  }
  if (opties.uitsplitsing.length > 0) {
    rijen.push(["Uitgesplitst op", opties.uitsplitsing.join(" · ")]);
  }
  // Altijd vermelden, ook als er geen vestiging is gekozen: dat de cijfers binnen jouw rechten
  // vallen is zelf een gegeven dat je later wilt weten.
  rijen.push(["Vestiging", opties.vestiging ?? "Alle waar je toegang toe hebt"]);
  rijen.push([]);
  return rijen;
}
